import {
	forwardRef,
	useEffect,
	useImperativeHandle,
	useRef,
	useState,
	type ImgHTMLAttributes,
} from "react";

interface CloudflareImageProps {
	isCdn?: boolean;
	loading?: "lazy" | "eager" | undefined;
}

function removeLeadingSlash(str: string | undefined): string | undefined {
	if (str?.startsWith("/")) {
		return str.slice(1);
	}
	return str;
}

export const CloudflareImage = forwardRef<
	HTMLImageElement,
	CloudflareImageProps & ImgHTMLAttributes<HTMLImageElement>
>(({ isCdn: useCdn, src, alt, loading = "lazy", ...props }, ref) => {
	const innerRef = useRef<HTMLImageElement>(null);

	useImperativeHandle(ref, () => innerRef.current as HTMLImageElement);

	const [isLoaded, setLoaded] = useState(false);

	useEffect(() => {
		if (!innerRef.current) {
			return;
		}
		if (innerRef.current.complete) {
			setLoaded(true);
		}
	}, []);

	if (useCdn) {
		if (src?.startsWith("https://imagedelivery.net")) {
			src = src.replace("https://imagedelivery.net/", "");
			const splitSrc = src.split("/");
			if (splitSrc.length !== 3) {
				throw new Error("Invalid image URL");
			}
			const [accountHash, imageId, variant] = splitSrc;
			return (
				<img
					ref={innerRef}
					{...props}
					src={`/cdn-cgi/imagedelivery/${accountHash}/${imageId}${variant}`}
					alt={alt}
					loading={loading}
					decoding="async"
					data-loaded={isLoaded}
					onLoad={() => setLoaded(true)}
					onError={() => setLoaded(true)}
					srcSet={`
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=260   260w,
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=320   320w,
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=640   640w,
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=960   960w,
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=1280 1280w,
						/cdn-cgi/imagedelivery/${accountHash}/${imageId}/quality=90,format=auto,fit=scale-down,width=1920 1920w
					`}
				/>
			);
		}
		src = removeLeadingSlash(src);
		return (
			<img
				ref={innerRef}
				{...props}
				alt={alt}
				loading={loading}
				decoding="async"
				data-loaded={isLoaded}
				onLoad={() => setLoaded(true)}
				onError={() => setLoaded(true)}
				srcSet={`
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=260/${src}   260w,
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=320/${src}   320w,
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=640/${src}   640w,
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=960/${src}   960w,
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=1280/${src} 1280w,
						/cdn-cgi/image/quality=90,format=auto,fit=scale-down,width=1920/${src} 1920w
					`}
			/>
		);
	}

	// biome-ignore lint/a11y/useAltText: Alt is provided from the parent
	return (
		<img
			ref={innerRef}
			alt={alt}
			loading="lazy"
			decoding="async"
			data-loaded={isLoaded}
			onLoad={() => setLoaded(true)}
			src={src}
			{...props}
		/>
	);
});
